import { CalendarIcon } from '@heroicons/react/20/solid';
import { MapPinIcon } from '@heroicons/react/24/outline';

import { createOrUpdateBorrower, getBorrower } from '@/apiCalls/borrower';
import { Button, EButtonType } from '@/components/Button';
import { PartnerLogo } from '@/components/PartnerLogo';
import {
    EVehicleAssetType,
    selectVehicleAssetType,
    selectVehicleBuildYear,
    selectVehicleMake,
    selectVehicleModel,
    selectVehicleVariant,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectAgreedToTermsAndConditions,
    selectPartner,
    setDisplay,
    setModal,
} from '@/context/display';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectIsLoggedIn } from '@/context/user';
import { CarIcon } from '@/icons/CarIcon';
import { TermsAndConditions } from '@/UserInput/TermsAndConditions';
import { TestDriveDate } from '@/UserInput/TestDriveDate';
import { TestDriveTime } from '@/UserInput/TestDriveTime';

// 'TestDriveLandingPage', not 'QuickCalculator'. Both screens exported the same id — a
// copy-paste that made this page indistinguishable from QuickCalculator in the DOM, so
// a query for either could match the wrong screen. This file's own test imports the
// constant so it passed regardless, which is why nothing caught it.
export const testId = 'TestDriveLandingPage';

export function TestDriveLandingPage(): JSX.Element {
    // ***** Redux *****
    const year = useAppSelector(selectVehicleBuildYear);
    const make = useAppSelector(selectVehicleMake);
    const model = useAppSelector(selectVehicleModel);
    const variant = useAppSelector(selectVehicleVariant);
    const assetType = useAppSelector(selectVehicleAssetType);
    const partner = useAppSelector(selectPartner);
    const { locationName, locationAddress, locationAddress2 } = partner;
    const agreedTerms = useAppSelector(selectAgreedToTermsAndConditions);
    const { isDefaultPrivacyAgreed } = agreedTerms;
    const hasAgreed = isDefaultPrivacyAgreed?.agreed;
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const dispatch = useAppDispatch();

    // ***** CONSTANTS *****
    const baseId = 'screen-TestDriveLandingPage';
    const vehicleName =
        [year, make, model, variant].filter(Boolean).join(' ') ||
        'Vehicle Details';

    const ridingTypes = [
        EVehicleAssetType.MOTORBIKES_REG,
        EVehicleAssetType.MOTORBIKES_UNREG,
        EVehicleAssetType.MOTORCYCLE,
    ];

    const caravanTypes = [
        EVehicleAssetType.CARAVAN,
        EVehicleAssetType.CARAVANS,
        EVehicleAssetType.CARAVAN_UNSEC,
    ];

    const title = ((): string => {
        if (assetType && ridingTypes.includes(assetType)) {
            return "Let's Go Riding!";
        } else if (assetType && caravanTypes.includes(assetType)) {
            return "Let's Go Camping!";
        } else {
            return "Let's Go Driving!";
        }
    })();

    async function handleClick(): Promise<void> {
        if (!hasAgreed) return;

        // Check for authentication - if not logged in, show lead capture modal
        if (!isLoggedIn) {
            dispatch(setModal(EModals.REGISTER));
            return;
        }

        // User is logged in, proceed with normal flow
        await getBorrower();
        await createOrUpdateBorrower();
        const state = store.getState();
        const { borrowerReducer } = state;
        if (
            borrowerReducer.personalDetails.licenseNumber !== '' &&
            borrowerReducer.personalDetails.licenseNumber !== undefined
        ) {
            dispatch(setDisplay(EDisplays.OCR_SUMMARY));
        } else {
            dispatch(setDisplay(EDisplays.IDENTIFICATION));
        }

        return;
    }

    // ***** Render *****
    return (
        <div className='relative w-full h-full' data-testid={testId}>
            <img src='/testdrive.png' className='filter brightness-50 w-full' />
            <div className='absolute left-1/2 top-[14%] transform -translate-x-1/2 w-[90%] rounded text-white text-center'>
                <h1 className='text-3xl font-bold'>{title}</h1>
                <p className='text-sm'>
                    To continue, confirm the details below
                    <br />
                    and agree to the terms & conditions.
                </p>
            </div>
            <div className='absolute left-1/2 top-[30%] transform -translate-x-1/2 w-[90%] rounded'>
                <div className='bg-white w-full rounded-lg p-4'>
                    {/* Logo */}
                    <div className='w-full flex-center pb-4'>
                        <PartnerLogo />
                    </div>
                    {/* Car Details */}
                    <div className='flex flex-col space-y-4'>
                        <p className='font-semibold '>Test Drive</p>
                        <div className='flex space-x-4'>
                            <CarIcon className='w-6 h-6' />
                            <p>{vehicleName}</p>
                        </div>
                        <div className='flex flex-col'>
                            <div className='flex space-x-4'>
                                <MapPinIcon className='w-6 h-6' />
                                <p>{locationName}</p>
                            </div>
                            <div className='ml-10 italic'>
                                <p>{locationAddress},</p>
                                <p>{locationAddress2}</p>
                            </div>
                        </div>
                    </div>
                    {/* Time Details */}
                    <div className='flex items-center'>
                        <CalendarIcon className='w-10 h-10 z-10' />
                        <div className='w-full mx-2 ml-4'>
                            <TestDriveDate />
                        </div>
                        <div className='w-full'>
                            <TestDriveTime />
                        </div>
                    </div>
                    <div className='w-full border h-[1px] my-4' />

                    {/* T&C */}
                    <TermsAndConditions baseId={baseId} />
                    {/* Button */}
                    <div className='py-2' />
                    <Button
                        type={
                            hasAgreed
                                ? EButtonType.PRIMARY
                                : EButtonType.FULL_ROUNDED_DISABLED
                        }
                        onClick={handleClick}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </div>
    );
}
